import { coerceBooleanValue } from '../../shared/utils/field-preload.utils';

export const FOREIGN_BOOLEAN_FIELDS = [
  'hasPublicJobManager',
  'isPepRelative',
  'hasGreenCard',
  'hasNothAmericanNationality',
  'isObligatedTaxOtherCountry',
  'isForeignResident',
  'hasStayedAbroad183Days',
] as const;

export const LOCKED_DEFAULT_FALSE_FIELDS = ['isPepRelative', 'hasStayedAbroad183Days'] as const;

export type ForeignBooleanField = typeof FOREIGN_BOOLEAN_FIELDS[number];

export function isLockedDefaultFalseField(formName: string | undefined): boolean {
  return LOCKED_DEFAULT_FALSE_FIELDS.some((field) => field === formName);
}

export function coerceForeignBoolean(value: unknown): boolean | null {
  return coerceBooleanValue(value);
}

export function hasAnsweredBoolean(value: unknown): boolean {
  return coerceForeignBoolean(value) !== null;
}

export function needsPublicJobDetails(value: unknown): boolean {
  return coerceForeignBoolean(value) === true;
}

export function needsPepRelativeDetails(value: unknown): boolean {
  return coerceForeignBoolean(value) === true;
}

export function needsTaxCountryDetails(value: unknown): boolean {
  return coerceForeignBoolean(value) === true;
}

export function arePublicJobDetailsValid(values: Record<string, unknown>): boolean {
  const position = String(values['publicJobPosition'] ?? '').trim();
  const startDate = values['publicJobStartDate'];
  return position.length >= 3 && !!startDate;
}

export function arePepRelativeDetailsValid(values: Record<string, unknown>): boolean {
  const fullName = String(values['pepRelativeFullName'] ?? '').trim();
  const documentType = values['pepRelativeDocumentType'];
  const documentNumber = String(values['pepRelativeDocumentNumber'] ?? '');
  const positionEntity = String(values['pepRelativePositionEntity'] ?? '').trim();

  return (
    fullName.length >= 7
    && documentType !== null
    && documentType !== undefined
    && documentType !== ''
    && documentNumber.length >= 6
    && documentNumber.length <= 10
    && /^[a-zA-Z0-9]+$/.test(documentNumber)
    && positionEntity.length >= 3
  );
}

export function isForeignFormComplete(
  values: Record<string, unknown>,
  options?: {
    isValidCountry?: boolean;
    tinFormats?: { regular_expression: string }[];
  }
): boolean {
  const allBooleansAnswered = FOREIGN_BOOLEAN_FIELDS.every((field) => hasAnsweredBoolean(values[field]));
  if (!allBooleansAnswered) return false;

  if (needsPublicJobDetails(values['hasPublicJobManager']) && !arePublicJobDetailsValid(values)) {
    return false;
  }

  if (needsPepRelativeDetails(values['isPepRelative']) && !arePepRelativeDetailsValid(values)) {
    return false;
  }

  if (needsTaxCountryDetails(values['isObligatedTaxOtherCountry'])) {
    if (!options?.isValidCountry) return false;
    const tin = String(values['tinNumber'] ?? values['tin'] ?? '').trim();
    if (!tin) return false;

    const formats = options.tinFormats ?? [];
    if (formats.length > 0) {
      const tinValid = formats.some((format) => {
        try {
          return new RegExp(format.regular_expression).test(tin);
        } catch {
          return false;
        }
      });
      if (!tinValid) return false;
    }
  }

  return true;
}

export function buildForeignSavePayload(
  values: Record<string, unknown>,
  countryCode: string | null = null
): Record<string, unknown> {
  const payload: Record<string, unknown> = { ...values };
  const tinValue = String(values['tinNumber'] ?? values['tin'] ?? '').trim();

  for (const field of FOREIGN_BOOLEAN_FIELDS) {
    payload[field] = coerceForeignBoolean(values[field]) === true;
  }

  if (!payload['hasPublicJobManager']) {
    payload['publicJobPosition'] = null;
    payload['publicJobStartDate'] = null;
    payload['publicJobEndDate'] = null;
  }

  if (!payload['isPepRelative']) {
    payload['pepRelativeFullName'] = null;
    payload['pepRelativeDocumentType'] = null;
    payload['pepRelativeDocumentNumber'] = null;
    payload['pepRelativePositionEntity'] = null;
  }

  if (!payload['isObligatedTaxOtherCountry']) {
    payload['obligatedTaxCountry'] = null;
    payload['obligatedTaxCountryId'] = null;
    payload['tin'] = null;
    payload['tinNumber'] = null;
  } else {
    payload['obligatedTaxCountry'] = countryCode ?? values['obligatedTaxCountry'] ?? null;
    payload['tin'] = tinValue || null;
    payload['tinNumber'] = tinValue || null;
  }

  return payload;
}
